import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import Layout from "../components/Layout";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { Send, ArrowLeft, Search, MessageCircle, Loader2 } from "lucide-react";

const MessagesPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await api.get("/conversations");
        setConversations(response.data);
      } catch (_error) {
        toast.error("Failed to load conversations");
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, []);

  // Load conversation from URL or select first one
  useEffect(() => {
    if (conversationId) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        setSelectedConversation(conv);
      }
    }
  }, [conversationId, conversations]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    const fetchMessages = async () => {
      try {
        const response = await api.get(`/conversations/${selectedConversation.id}/messages`);
        setMessages(response.data);
      } catch (_error) {
        toast.error("Failed to load messages");
      }
    };
    
    fetchMessages();
    
    // Poll for new messages every 3 seconds
    pollIntervalRef.current = setInterval(fetchMessages, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Search users
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const searchUsers = async () => {
      setSearching(true);
      try {
        const response = await api.get(`/users/search/${searchQuery}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error("Search failed", error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/messages/${conv.id}`);
  };

  const handleStartConversation = async (otherUser) => {
    try {
      const response = await api.post("/conversations", { participant_id: otherUser.id });
      const newConv = response.data;
      
      // Add to conversations if not exists
      if (!conversations.find(c => c.id === newConv.id)) {
        setConversations(prev => [newConv, ...prev]);
      }
      
      setSelectedConversation(newConv);
      navigate(`/messages/${newConv.id}`);
      setSearchQuery("");
      setSearchResults([]);
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const response = await api.post(`/conversations/${selectedConversation.id}/messages`, {
        content: newMessage
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
      
      // Update conversation's last message
      setConversations(prev => prev.map(c => 
        c.id === selectedConversation.id 
          ? { ...c, last_message: newMessage, last_message_at: response.data.created_at }
          : c
      ));
    } catch (_error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getInitials = (otherUser) => {
    if (otherUser?.username) {
      return otherUser.username.slice(0, 2).toUpperCase();
    }
    return "??";
  };

  return (
    <Layout hideBottomNav>
      <div className="h-[calc(100vh-4rem)] flex" data-testid="messages-page">
        {/* Conversations List - Hidden on mobile when conversation is selected */}
        <div className={`w-full md:w-80 border-r border-border flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-border">
            <h1 className="text-xl font-bold mb-4">Messages</h1>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
            
            {/* Search Results */}
            {searchQuery && (
              <div className="absolute z-10 mt-2 w-[calc(100%-2rem)] md:w-72 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
                {searching ? (
                  <div className="p-4 flex justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(u => (
                    <button
                      key={u.id}
                      onClick={() => handleStartConversation(u)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left"
                      data-testid={`search-result-${u.id}`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={u.avatar} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(u)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{u.username || "Anonymous"}</p>
                        <p className="text-sm text-muted-foreground">{u.phone}</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="p-4 text-center text-muted-foreground">No users found</p>
                )}
              </div>
            )}
          </div>
          
          {/* Conversations */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground" data-testid="no-conversations">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
                <p className="text-sm">Search for users to start chatting</p>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full flex items-center gap-3 p-4 hover:bg-muted transition-colors text-left border-b border-border ${
                    selectedConversation?.id === conv.id ? 'bg-muted' : ''
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={conv.other_user?.avatar} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(conv.other_user)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <p className="font-medium truncate">
                        {conv.other_user?.username || "Anonymous"}
                      </p>
                      {conv.last_message_at && (
                        <span className="text-xs text-muted-foreground">
                          {formatTime(conv.last_message_at)}
                        </span>
                      )}
                    </div>
                    {conv.last_message && (
                      <p className="text-sm text-muted-foreground truncate">
                        {conv.last_message}
                      </p>
                    )}
                  </div>
                </button>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="h-16 border-b border-border flex items-center gap-3 px-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden"
                  onClick={() => {
                    setSelectedConversation(null);
                    navigate("/messages");
                  }}
                  data-testid="back-to-conversations"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                
                <button 
                  onClick={() => navigate(`/user/${selectedConversation.other_user?.id}`)}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  data-testid="chat-user-profile"
                >
                  <Avatar>
                    <AvatarImage src={selectedConversation.other_user?.avatar} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(selectedConversation.other_user)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-semibold">
                    {selectedConversation.other_user?.username || "Anonymous"}
                  </span>
                </button>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} message-bubble`}
                        style={{ animationDelay: `${idx * 0.02}s` }}
                      >
                        <div
                          className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                            isOwn 
                              ? 'bg-primary text-primary-foreground rounded-br-md' 
                              : 'bg-muted text-foreground rounded-bl-md'
                          }`}
                          data-testid={`message-${msg.id}`}
                        >
                          <p className="break-words">{msg.content}</p>
                          <p className={`text-xs mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1"
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    disabled={!newMessage.trim() || sendingMessage}
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground" data-testid="no-chat-selected">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose from your existing conversations or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MessagesPage;
